import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Box, Alert } from "@mui/material";
import { successToast, errorToast } from "../../Utils/toast";
import { useLoader } from "./../common/LoaderContext";
import { useMomApi } from "../../Services/api/momApi";
import dayjs from "dayjs";

interface DialogBoxProps {
  openMomDialog: boolean;
  setOpenMomDialog: (open: boolean) => void;
  momData: any;
  userData: any;
  filters: any;
  landscapeId: string;
  CBTType: string;
}

const MomModalPopUp: React.FC<DialogBoxProps> = ({
  openMomDialog,
  setOpenMomDialog,
  momData,
  userData,
  filters,
  landscapeId,
  CBTType,
}) => {
  const [momComments, setMomComments] = useState<string>("");
  const [originalComments, setOriginalComments] = useState<string>("");
  const { setLoading } = useLoader();
  const { saveMomComments } = useMomApi();
  const [validationError, setValidationError] = useState<string>("");

  useEffect(() => {
    if (openMomDialog && momData && momData.length > 0) {
      const existingMOM = momData[0].MOM || "";
      setMomComments(existingMOM);
      setOriginalComments(existingMOM);
    } else if (openMomDialog) {
      // If no existing data, start with empty string
      setMomComments("");
      setOriginalComments("");
    }
  }, [openMomDialog, momData]);
  const handleConfirm = async () => {
    if (!momComments || momComments.trim() === "") {
      setValidationError(
        "Comments are required. Please enter your comments before saving."
      );
      return;
    }

    setValidationError("");
    try {
      setLoading(true);

      // Prepare the API payload
      const apiPayload = {
        LandscapeID: landscapeId,
        UserID: userData?.UserID,
        CompanyCodes: Array.isArray(filters.companycodes)
          ? filters.companycodes.join(",")
          : filters.companycodes || "",
        CountryCode: filters.country,
        BSReportingYear: filters.reportingyear,
        BSReportingMonth: filters.reportingmonth,
        BSComparisonYear: filters.comparisonyear,
        BSComparisonMonth: filters.comparisonmonth,
        PLReportingYear: filters.plreportingyear,
        PLReportingMonth: filters.plreportingmonth,
        PLComparisonYear: filters.plcomparisonyear,
        PLComparisonMonth: filters.plcomparisonmonth,
        MOM: momComments.trim(),
      };

      const response = await saveMomComments(apiPayload);

      if (response) {
        successToast("MOM comments saved successfully!");
        setOpenMomDialog(false);

        setMomComments("");
        setOriginalComments("");
      } else {
        errorToast("Failed to save MOM comments. Please try again.");
      }
    } catch (error) {
      console.error("Error saving MOM comments:", error);
      errorToast("Failed to save MOM comments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMomComments(e.target.value);
    if (validationError) {
      setValidationError("");
    }
  };

  const handleCancel = () => {
    // Reset to original value when canceling
    setMomComments(originalComments);
    setValidationError("");
    setOpenMomDialog(false);
  };

  const getDialogText = (dialogAction: string | null) => {
    return {
      heading: "",
      description: "",
    };
  };
  const formatDate = (value?: string) => {
    if (!value) return "";
    const d = dayjs(value);
    if (!d.isValid()) return String(value);
    return d.format("DD/MM/YYYY HH:mm");
  };
  const { heading, description } = getDialogText("Mom");
  return (
    <>
      <Dialog
        open={openMomDialog}
        onClose={() => setOpenMomDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            height: "60vh",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle>MOM Comments</DialogTitle>
        <DialogContent
          sx={{
            padding: "20px 24px",
          }}
        >
          <DialogContentText>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.8rem",
              }}
            >
              <b>Submitted By: {momData?.[0]?.SubmittedBy}</b>
              <b>Submitted Date(UTC): {formatDate(momData?.[0]?.SubmittedDate)}</b>
            </Box>
          </DialogContentText>
          {validationError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {validationError}
            </Alert>
          )}
          <textarea
            maxLength={4000}
            onChange={handleCommentsChange}
            value={momComments}
            style={{
              width: "100%",
              height: "80%",
              minHeight: "20px",
              fontSize: "1rem",
              padding: "2px 2px",
              lineHeight: "1",
              border: "1px solid #999",
              borderRadius: "4px",
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          <div
            style={{
              textAlign: "right",
              fontSize: "1rem",
              color: "#666",
              marginTop: "4px",
            }}
          >
            {momComments.length}/4000 characters
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="btn-optional"
            onClick={handleCancel}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            className="btn-mandatory"
            onClick={handleConfirm}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MomModalPopUp;
